import { createNamespacedHelpers } from 'vuex';
import Constants from '@/constants/Constants.js';

import VueCropper from 'vue-cropperjs';
import 'cropperjs/dist/cropper.css';
import showToast from '@/util/toast';
import { HttpStatusCode } from 'axios';

import ModalPromptSuggestion from '@/components/shared/ModalPromptSuggestion/ModalPromptSuggestion.vue';

const globalMapper = createNamespacedHelpers('global');
const advancedSettingMapper = createNamespacedHelpers('global/advancedSetting');
const authMapper = createNamespacedHelpers('global/auth');
const advancedSetting = createNamespacedHelpers('global/advancedSetting');
const imageMapper = createNamespacedHelpers('detector/image');
const detectorMapper = createNamespacedHelpers('detector');
const cameraMapper = createNamespacedHelpers('detector/camera');
const modelMapper = createNamespacedHelpers('detector/model');
const DETECTED_IMAGE_LIMIT = 10;
const AUTO_CLOSE_TOGGLE_INDEX = 1;

let audio = new Audio(require('@/constants/sounds/shutter.mp3'));
const historyMapper = createNamespacedHelpers('history');

export default {
	components:{
		VueCropper,
		ModalPromptSuggestion,
	},
	data() {
		return {
			camera: null,
			canvas: null,
			stream: null,
			timer: null,
			show: true,
			countdown: 0,
			height: document.documentElement.clientHeight,
			width: document.documentElement.clientWidth,
			slidePoint: 3,
			onPressEnter: false,

			HD_IMAGE_SIZE: Constants.HD_IMAGE_SIZE,
			onInterval: false,
			showThumbnail: true,
			onDetectionDone: true,
			onCaptureDisabled: false,
			cameraId: null,
			isShowSelectionTool: false,
			isShowCropper: false,
			imageCropURI: null,
			selectedModel: null,
			isShowPromptSuggestion: false,
			modalId: "promptSuggestion"
		};
	},
	async mounted() {
		await this.closePreviewImage();
		this.loadVideoMetadata();
		const cameraId = await this.getCameraId();
		this.cameraId = cameraId;
		this.openCamera(this.mode, this.cameraId);
		this.eventBusListeners();

		document.addEventListener('keydown', (event) => {
			if (event.key === 'Enter' && this.$route.fullPath === '/camera') {
				if (this.modalsHidden && this.onDetectionDone) {
					this.onCaptureButtonClick();
				}
			}
		});
		// occurs when an video resumes after beeing paused or buffered
		this.$refs.camera.addEventListener('playing', () => {
			this.onDetectionDone = true;
		});
	},
	async beforeDestroy() {
		this.closeCamera();
	},
	computed: {
		...globalMapper.mapState(['spinner']),
		...advancedSettingMapper.mapState(['advancedSetting']),
		...authMapper.mapState(['username']),
		...modelMapper.mapState(['model','models', 'displayCrossHair']),
		...detectorMapper.mapState([
			'ok',
			'imageURI',
			'isVisible',
			'imagePreviewVisible',
			'showCamera',
			'showCameraImageBorder',
			'selectedImage',
			'isPreviewImageDetection',
		]),
		...cameraMapper.mapState([
			'auto',
			'mode',
			'sliderPoint',
			'focus',
			'showImageFromCloud',
		]),
		...advancedSetting.mapState(['advancedSetting']),
		crosshair() {
			return this.focus;
		},

		buttonDisabled() {
			if (this.model) {
				return false;
			}
			return true;
		},
		modalsHidden() {
			return !this.isVisible && !this.imagePreviewVisible;
		},

		autoCloseDetectedImgToggle() {
			return this.advancedSetting.detector[AUTO_CLOSE_TOGGLE_INDEX].settingContent;
		},
	},

	watch: {
		mode(value) {
			this.openCamera(value, this.cameraId);
		},

		slidePoint: function (val) {
			this.$store.dispatch('detector/camera/setSliderPoint', val);
		},

		// check khi nào tắt slider thì tắt luôn nút stop
		auto: function (val) {
			if (val === false) {
				this.onInterval = false;
			}
		},
		model: function (value) {
			this.selectedModel = this.models.find(({ model_name }) => model_name === value);
			let typeShot = this.selectedModel.options?.type_shot;
			let textPrompt = this.selectedModel.options?.TEXT_PROMPT;
			// check type model for display Selection tool
			if (typeShot === 'one-shot') {
				this.isShowSelectionTool = true;
				this.isShowPromptSuggestion = false;
			} else if (textPrompt != null && value == "CounTX_auto_prompt"){
				this.isShowSelectionTool = false;
				this.isShowPromptSuggestion = true;
			} else {
				this.isShowSelectionTool = false;
				this.isShowPromptSuggestion = false
			}
		},
	},

	methods: {
		...globalMapper.mapActions(['setSpinner']),
		...detectorMapper.mapActions(['runDetection']),
		...detectorMapper.mapMutations([
			'SET_SHOW_CAMERA',
			'SET_SHOW_CAMERA_IMG_BORDER',
			'SET_PREVIEW_IMAGE_DETECTION',
			'SET_IMAGE_URI',
		]),
		...imageMapper.mapActions([
			'uploadImage',
			'retrieveImages',
			'onDownload',
			'presignUrlS3',
		]),
		...historyMapper.mapMutations(['UPDATE_NO_CHECKBOX']),
		...cameraMapper.mapMutations(['SHOW_IMAGE_FROM_CLOUD']),
		...cameraMapper.mapActions(['setIsDetecting']),
		...advancedSettingMapper.mapActions(['getCameraId']),
		...modelMapper.mapActions(['updateReferenceImage']),

		loadVideoMetadata() {
			const videoElement = this.$refs.camera;
			videoElement.addEventListener('loadedmetadata', () => {
				this.showThumbnail = false;
			});
		},

		async delay(ms) {
			return new Promise((resolve) => setTimeout(resolve, ms));
		},

		async closePreviewImage() {
			this.SET_SHOW_CAMERA(false);
			this.SET_SHOW_CAMERA_IMG_BORDER(false);
			this.setIsDetecting(false);

			await this.delay(100);

			this.SET_PREVIEW_IMAGE_DETECTION(false);
			this.UPDATE_NO_CHECKBOX(false);
			this.SET_IMAGE_URI('');
			this.SET_SHOW_CAMERA(true);
			this.$refs.camera.play();
			this.SET_SHOW_CAMERA_IMG_BORDER(true);
			this.onCaptureDisabled = false;
		},

		/**
		 * Camera button click handler
		 */
		async onCaptureButtonClick() {
			if (!this.onDetectionDone) {
				return;
			}

			if (this.auto) {
				await this.execute();
				return;
			}

			if (this.isPreviewImageDetection) {
				await this.runDetectionFromSelectedImage();
				return;
			}

			await this.capture();
		},

		/**
		 * Icon start clicking event handler
		 */
		onStartInterval() {
			this.onInterval = true;
			this.onCaptureButtonClick();
		},

		/**
		 * Icon stop clicking event handler
		 */
		onStopInterval() {
			this.onInterval = false;
		},

		/**
		 * countdown interval with capture function event handler
		 */
		async execute() {
			let lastShot = 0;

			/**
			 *  kiểm tra khi mà trong lúc countdown,
			 *  nút màu đỏ mà biến thành màu xanh thì ngưng countdown
			 */
			while (this.onInterval) {
				this.countdown = this.sliderPoint;

				for (let index = this.countdown; index > 0; index--) {
					if (!this.onInterval) {
						break;
					}
					this.countdown -= 1;
					await this.sleep(1000);
				}

				// Kiểm tra lại trước khi thực hiện capture
				if (this.onInterval) {
					if (this.showImageFromCloud) {
						await this.runDetectionFromSelectedImage();
					} else {
						await this.capture();
					}
					lastShot += 1;
					console.log('number of shot:', lastShot);

					if (this.autoCloseDetectedImgToggle === false) {
						await this.sleep(1000);

						this.closePreviewImage();
					}
				}
			}
		},

		/**
		 * capture and upload the image to the server event
		 */
		async capture() {
			// this.$el.classList.remove('imageFromCloud');
			this.onDetectionDone = false;
			this.$refs.camera.pause();
			audio.play();
			this.setSpinner(true);

			try {
				let context = this.$refs.canvas.getContext('2d');
				let width = this.HD_IMAGE_SIZE.WIDTH;
				let height = this.HD_IMAGE_SIZE.HEIGHT;

				context.drawImage(this.$refs.camera, 0, 0, width, height);

				let uri = this.$refs.canvas.toDataURL('image/jpg');
				let file = await this.convertImageToBinary(uri);

				await this.uploadImage({ file: file, username: this.username });
				this.setIsDetecting(true);

				await this.runDetection({
					fileName: file.name,
					username: this.username,
					model: this.model,
				});
			} finally {
				this.setSpinner(false);
				this.SET_SHOW_CAMERA(false);
				this.onCaptureDisabled = true;

				this.retrieveImages({
					username: this.username,
					model: this.model,
					limit: DETECTED_IMAGE_LIMIT,
				});

				if (this.autoCloseDetectedImgToggle === true) {
					this.SET_SHOW_CAMERA(false);

					await this.sleep(3000);
					this.$refs.camera.play();

					this.SET_SHOW_CAMERA(true);
					this.SET_IMAGE_URI('');
					this.onCaptureDisabled = false;
					this.setIsDetecting(false);
				}
			}
		},

		async runDetectionFromSelectedImage() {
			this.$refs.camera.pause();
			this.setSpinner(true);
			this.onCaptureDisabled = false;

			try {
				let payload = {
					file_name: this.selectedImage.image_name,
					s3_key: this.selectedImage.s3_key,
				};
				let response = await this.presignUrlS3(payload);
				const base64 = await this.onDownload(response.data.link);
				let file = await this.convertImageToBinary(base64);
				await this.uploadImage({ file: file, username: this.username });

				this.setIsDetecting(true);
				// image_key is used to download the image if it doesn't exists
				await this.runDetection({
					fileName: file.name,
					username: this.username,
					model: this.model,
					image_key: this.selectedImage.s3_key,
				});
			} finally {
				this.setSpinner(false);
				this.SHOW_IMAGE_FROM_CLOUD(false);
				this.onCaptureDisabled = true;

				this.SET_SHOW_CAMERA(false);

				this.retrieveImages({
					username: this.username,
					model: this.model,
					limit: DETECTED_IMAGE_LIMIT,
				});

				if (this.autoCloseDetectedImgToggle === true) {
					this.SET_SHOW_CAMERA(false);
					this.SET_SHOW_CAMERA_IMG_BORDER(true);
					await this.sleep(3000);
					this.$refs.camera.play();

					this.SET_SHOW_CAMERA(true);
					this.setIsDetecting(false);
				} else {
					this.SET_SHOW_CAMERA_IMG_BORDER(true);
				}

				this.SET_PREVIEW_IMAGE_DETECTION(false);

				if (this.autoCloseDetectedImgToggle) {
					this.SET_IMAGE_URI('');
					this.onCaptureDisabled = false;
				} else {
					this.onCaptureDisabled = true;
				}
			}
		},

		eventBusListeners() {
			this.$eventBus.$on('startDetectionFromSelectImage', () => {
				// this.runDetectionFromSelectedImage();
			});
			this.$eventBus.$on('playCamera', () => {
				if (this.$refs.camera) {
					this.$refs.camera.play();
				}
			});
		},

		onCropImage() {
			if (!this.isShowSelectionTool) {
				return
			}
			// capture image to crop
			if (this.showImageFromCloud) {
				this.imageCropURI = this.imageURI
			}
			else {
				let context = this.$refs.canvas.getContext('2d');
				let width = this.HD_IMAGE_SIZE.WIDTH;
				let height = this.HD_IMAGE_SIZE.HEIGHT;
				context.drawImage(this.$refs.camera, 0, 0, width, height);
	
				this.imageCropURI = this.$refs.canvas.toDataURL('image/jpg');
			}
			
			// replace image and show to crop
			this.$refs.cropper.replace(this.imageCropURI)
			this.isShowCropper = true

		},

		async onCropEnd() {
			// get data from cropper
			let data = this.$refs.cropper.getData();
			let cropped_img = this.$refs.cropper.getCroppedCanvas().toDataURL('image/png');
			this.$refs.refImage.style.backgroundImage = `url(${cropped_img})`;
			this.isShowCropper = false
			// convert data to file
			let reference_file = await this.convertImageToBinary(cropped_img);
			// update reference image for one-shot learning
			let response = await this.updateReferenceImage({
				modelId: this.selectedModel._id.$oid,
				file: reference_file,
				bounding_box: JSON.stringify([
					Math.floor(data.x),
					Math.floor(data.y),
					Math.floor(data.x + data.width),
					Math.floor(data.y + data.height)
				])
			});
			// show toast
			if (response.status != HttpStatusCode.Ok) {
				showToast({
					vueInstance: this,
					type: 'error',
					message: this.$t('fail'),
				});
    
        return;
			}
			showToast({
				vueInstance: this,
				type: 'success',
				message: this.$t('success'),
			});
		},

		onShowModalPromptSuggestion() {
			if (!this.isShowPromptSuggestion) {
				return
			}
			this.$bvModal.show(this.modalId);
		},
		/**
		 * Get Image at camera screen
		 * @returns Image at camera screen when call this function
		 */
		async getPredictionImage() {
			let predictionImage = null
			if (this.showImageFromCloud) {
				predictionImage = this.imageURI

				const predictionImageBase64 = await this.onDownload(predictionImage);
				predictionImage = await this.convertImageToBinary(predictionImageBase64);
			}
			else {
				let context = this.$refs.canvas.getContext('2d');
				let width = this.HD_IMAGE_SIZE.WIDTH;
				let height = this.HD_IMAGE_SIZE.HEIGHT;
				context.drawImage(this.$refs.camera, 0, 0, width, height);
				
				predictionImage = this.$refs.canvas.toDataURL('image/jpg');
				console.log(predictionImage)
				predictionImage = await this.convertImageToBinary(predictionImage);
			}
			return predictionImage
		}
	},
};
